import React, {createRef, Fragment, useEffect, useState} from 'react';
import {useQueryClient} from "react-query";
import moment from "moment";
import momentTz from "moment-timezone";
import __ from "lodash";
import axios from "axios";
import { Grid } from "@material-ui/core";

import {MaskedText, FormDialog, FormSelect, ComboSelect} from "../../../controls";
//import ScheduledTripLegEditor from "./ScheduledTripLegEditor";
import Validator from "../validators/AddCostingValidator";
import useTripDepartmentUsers from "../../../services/auth/useTripDepartmentUsers";
import { useCostCategories } from '../../../services/costings';

import AircraftLookup from '../../Aircraft/components/AircraftLookup';
import BuildPrimaryRouteFromLegs from '../../Trips/utils/BuildPrimaryRouteFromLegs';
import BuildOnwardRoutesFromLegs from '../../Trips/utils/BuildOnwardRoutesFromLegs';
import BuildTruckRoutesFromLegs from '../../Trips/utils/BuildTruckRoutesFromLegs';
import CostingSectorsEditor from './CostingSectorsEditor';
import {fetch as fetchCargo} from '../../../services/costings/useCostingCargo';
import {fetch as fetchSectors} from '../../../services/costings/useCostingSectors';
import {fetch as fetchServices} from '../../../services/costings/useCostingServices';
import {fetch as fetchCommissions} from '../../../services/costings/useCostingCommissions';
import FlightCostsView from './FlightCostsView';
import CostingCargoEditor from './CostingCargoEditor';
import CostingCommissionEditor from './CostingCommissionEditor';
import ActionsBar from '../../../controls/ActionsBar';
import { buildLegsFromSuggestion, rebuildPayloads, rebuildRevenue, rebuildService, rebuildTotals, runSectorJetPlan, handleServicesChanged, handleCommissionsChanged, queueSectorChanges, handleFlightCostsChanged, populateCostFilters } from '../includes/costingDialogFunctions';
import CostingGroundEditor from './CostingGroundEditor';
import CostingRevenueEditor from './CostingRevenueEditor';
import CostingOtherSectorsEditor from './CostingOtherSectorsEditor';
import calculateOptimalRoute from '../includes/calculateOptimalRoute';
import {fetch as fetchAcmiRate} from '../../../services/aircraft/useBlendedRate';
import CostingTotals from './CostingTotals';
import { useSettings } from '../../../services/settings';
import RouteSelector from './RouteSelector';
import GetRouteEndpointDialog from './GetRouteEndpointDialog';
import ViewFuelAnalysis from './ViewFuelAnalysis';
import {useAuth0} from "@auth0/auth0-react";
import QuickRoute from './QuickRoute';
import { defaultCostFilters } from '../includes/costingDialogFunctions';

const emptyCosting = {
  costingDate: moment.utc().toISOString(),
  routeWinds: process.env.REACT_APP_DEFAULT_WINDCODE || 'GRIB-UKO-WX',
  routeWindsMonth: "",
  legs:[],
  cargo: [],
  services: [],
  commissions: [],
  weightUom: "KG",
  contingencyFuelCostRate:0,
  contingencyBlockHoursRate:0,
  costFilters: {
    ...defaultCostFilters
  }
}

const rebuildServicesQueue = [];

const AddCostingDialog = (props) => {
  const {SystemData, apiSitePrefix, extraKeys, handleAdd, classes, defaultValues} = props;
  const requestRef = createRef(0);
  const eventBus = SystemData.eventBus;
  const ownerDepartment = defaultValues?.ownerDepartment||'COMMERCIAL';

  const {  user } = useAuth0();
  console.log(user);

  if (!__.isNil(defaultValues?.contingencyFuelCostRate)) emptyCosting['contingencyFuelCostRate'] = defaultValues.contingencyFuelCostRate;
  if (!__.isNil(defaultValues?.contingencyBlockHoursRate)) emptyCosting['contingencyBlockHoursRate'] = defaultValues.contingencyBlockHoursRate;

  const [dialogStates, setDialogStates] = useState({
    showDialog: false,
    record: __.cloneDeep(emptyCosting),
    title: 'Add Costing',
  });

  const formId = 'AddCosting';

  const [rebuildRunning, setRebuildRunning] = useState(false);
  const [filtersEnabled, setFiltersEnabled] = useState(false);

  const dateFormat = "DD/MM/YYYY";

  const queryClient = useQueryClient();
  const departmentUsers = useTripDepartmentUsers({queryClient, extraKeys: [ownerDepartment]});
  const costCategories = useCostCategories({queryClient, extraKeys: [], params: {level: "COSTING", includeGroup: 1}})
  const costingErrors = useSettings({queryClient, extraKeys: ["COSTING ERRORS"], useCache: true});

  useEffect(()=> {
    const toggleFilters = (state) => {
      const newFiltersEnabled = !filtersEnabled;
      setFiltersEnabled(newFiltersEnabled);
    }
    const recalculate = async (state) => {
      if (rebuildRunning || !costingErrors || !costCategories) return;
      const {record, recordChange} = state;
      const aircraftReg = record?.aircraftReg;

      fetchAcmiRate({queryClient, extraKeys:[aircraftReg, record.costingDate], useCache: true}).then((acmiRate) => {
        const {legs, cargo} = rebuildPayloads(record.legs, record.cargo);
        const oldLegList = Array.isArray(legs) && legs.length > 0?[legs[0].originAirportCode, ...legs.map(leg => leg.destinationAirportCode)]:[];
        for (let i = 0; i < legs.length; i++) {
          legs[i].fuelPolicyType = '';
          legs[i].jetPlanStatus = '';
          runSectorJetPlan(record, legs[i], apiSitePrefix);
        }
        let services = __.cloneDeep(record?.services);
        const legsClone = __.cloneDeep(legs);
        const {contingencyFuelCostRate, contingencyBlockHoursRate} = record;
        queryClient.invalidateQueries(['AirportCosts']);
        queryClient.invalidateQueries(['AirportCosts', '_cached']);
        rebuildServicesQueue.push({oldLegList, legs: legsClone, services, aircraftReg, recordChange, cargo, changes:{aircraftReg, acmiRate}, contingencyFuelCostRate, contingencyBlockHoursRate, costingDate: record.costingDate, costFilters: record.costFilters});
        recordChange({aircraftReg, acmiRate, legs, cargo});
        setTimeout(processServicesRebuild, 1);
      }).catch(err => {
        if (err?.response?.data?.error && err?.response?.data?.errorMessage) {
          SystemData.alert("Error", `${err?.response?.data?.errorMessage}`);
        } else {
          SystemData.alert("Error", `An unexpected error has occurred after changing the aircraft`);
        }
      })
    }

    const open = async (_record) => {
      const title = "Add New Costing";
      let record = {...__.cloneDeep(emptyCosting), ..._record};
      if (!__.has(record, 'aircraftReg') && defaultValues?.defaultAircraft) {
        record['aircraftReg'] = defaultValues.defaultAircraft;
        try {
          const acmiRate = await fetchAcmiRate({queryClient, extraKeys:[record.aircraftReg, record.costingDate], useCache: true});
          record['acmiRate'] = acmiRate;
        } catch(err) {
          SystemData.alert("Error", `An unexpected error has occurred getting the aircraft blended rate.`);
          return;
        }
        const acmiRate = await fetchAcmiRate({queryClient, extraKeys:[record.aircraftReg, record.costingDate], useCache: true});
        record['acmiRate'] = acmiRate;
      }
      if (costingErrors && costCategories) {
        const {legs, cargo} = rebuildPayloads(record.legs, record.cargo);
        record = {...record, legs, cargo};
        const costFilters = populateCostFilters(record, costCategories);
        record = {...record, costFilters};

        for (let i = 0; i < legs.length; i++) {
          runSectorJetPlan(record, legs[i], apiSitePrefix);
        }
        const {contingencyFuelCostRate, contingencyBlockHoursRate, services, aircraftReg} = record;
        rebuildServicesQueue.push({oldLegList: [], legs: legs, services, aircraftReg, cargo, contingencyFuelCostRate, contingencyBlockHoursRate, costingDate: record.costingDate, costFilters: record.costFilters});
        setTimeout(processServicesRebuild, 30);
      }

      if (user && departmentUsers && departmentUsers.data) {
        const departmentUser = __.find(departmentUsers.data, {user_id: user.sub});
        if (departmentUser)
          record["commercialOwnerId"] =  user.sub
      }

      setDialogStates(
        {
          ...dialogStates,
          showDialog:true,
          title,
          record: __.cloneDeep(record || emptyCosting),
        }
      );
    }

    const close = () => {
      setDialogStates({...dialogStates, showDialog:false, record: __.cloneDeep(emptyCosting), title: 'Add Costing'});
    }

    const quickRoute = (state) => {
      const {record} = state;
      const {cargo, aircraftReg, sectors} = record;
      eventBus.trigger('QuickRoute.open',{cargo, aircraftTailNumber: aircraftReg, sectors});
    }

    const calculateRoute = (state) => {
      const {record} = state;
      eventBus.trigger('GetRouteEndpointDialog.open',{record});
    }

    const buildRoute = (state) => {
      const {record, finalDestinationAirport} = state;
      const {cargo, aircraftReg, legs} = record;
      const legList = Array.isArray(cargo) && cargo.length > 0?__.uniq([cargo[0].originAirportCode, ...cargo.map(consignment => consignment.destinationAirportCode)]):[];

      if (finalDestinationAirport) {
        legList.push(finalDestinationAirport.airportCode);
      }

      if (legList.length > 7) {
        SystemData.alert('Route Too Complex', 'The number of legs is too high for the suggested route process');
        return;
      }

      if (legList.length > 6) {
        SystemData.confirm(
          `Route is Complex`,
          `The route contains a lot of airports, the suggested route may take a few minutes to produce, do you wish to continue?'`,
          [
            {icon: "check_circle", value: true, label: "Yes", options: {color:"primary"}},
            {icon: "cancel", value: false, label: "No", options: {color:"secondary"}}
          ],
          (response) => {
            if (response) {
              calculateOptimalRoute(aircraftReg, cargo, queryClient, eventBus, 'AddCostingDialog', finalDestinationAirport).then((options) => {
                setTimeout(()=> {
                  eventBus.trigger('RouteSelector.open',{options, status: "DONE", progress: null, legsExist: Array.isArray(legs) && legs.length > 0});
                }, 1);
              });
            }
          }
        );
      } else {
        calculateOptimalRoute(aircraftReg, cargo, queryClient, eventBus, 'AddCostingDialog', finalDestinationAirport).then((options) => {
          setTimeout(()=> {
            eventBus.trigger('RouteSelector.open',{options, status: "DONE", progress: null, legsExist: Array.isArray(legs) && legs.length > 0});
          }, 1);
        });
      }
    }
    const calculateRouteProgress = (state) => {
      const {progress} = state;
      eventBus.trigger('RouteSelector.open',{options: null, status: "BUILDING", progress});
    }

    const viewFuelAnalysis = (state) => {
      const {record} = state;
      eventBus.trigger('ViewFuelAnalysis.open', {record});
    }

    const runFuelAnalysis = (state) => {
      const {recordChange} = state;
      // console.log(JSON.stringify(__.omit(state?.record||{}, ['tableData']),null,2));

      const sectors = (state?.record?.legs||[]).map(leg => ({...__.pick(leg, [
        "costingSectorUuid",
        "originAirportCode",
        "destinationAirportCode",
        "alternateAirportCode",
        "legType",
        "fuelPrice",
        "noFuel",
        "fuelSupplier",
      ]), "tripLegUuid": leg.costingSectorUuid, "aircraftId": state.record.aircraftReg}));

      const sectorPayloadTotals = (state?.record?.legs||[]).map(leg => leg.payloadOnboard);

      // console.log(JSON.stringify({sectors, sectorPayloadTotals, costingDate: state.record.costingDate}, null, 2));

      const record = {sectors, sectorPayloadTotals, costingDate: state.record.costingDate};

      SystemData.toggleLoadingProgress(true);
      axios.put(`${apiSitePrefix}/costings/runFuelAnalysis`, record).then(response => {
        if (!response.data) {
          SystemData.alert("Error", `Fuel analysis failed to run. Server is not responding`);
          SystemData.toggleLoadingProgress(false);
        } else if (response.data.error) {
          SystemData.alert("Error", `Fuel analysis failed to run. ${response.data.error}`);
          SystemData.toggleLoadingProgress(false);
        } else if (!Array.isArray(response.data)) {
          SystemData.alert("Error", `Fuel analysis failed to run. An unexpected error has occurred.`);
          SystemData.toggleLoadingProgress(false);
        } else {
          const fuelData = response.data;

          const {contingencyFuelCostRate, contingencyBlockHoursRate} = state.record;
          let acmiRate = state.record.acmiRate;

          let jetPlanStatus = 'COMPLETED';

          const legs = state.record.legs.map((leg, i) => {
            const fuel = fuelData.find(fuel => fuel.costingSectorUuid === leg.costingSectorUuid);
            if (fuel.jeppsParams && fuel.jeppsParams.acmiRate) {
              acmiRate = fuel.jeppsParams.acmiRate;
            }
            const legChanges = __.pick(fuel.planData, [
              'fuelToUplift', 'tankeredExtraFuel', 'possibleTankerFuel', 'tankerFuelFactor', 'tankerCostPerUSG', 'dontTryTankering', 'fuelToCost', 'windData', 'blockHours',
              'fuelBurn', 'fuelBurnKg', 'fod', 'overflightCost', 'acmiCost', 'fuelPolicyType', 'tanker', 'extraFuelCost', 'fuelCost', 'permitCosts', 'permitDetails', 'useTanker', 'status', 'flightJetPlanUuid', 'optionRoute', 'optionRouteText',
            ]);
            if (legChanges.status !== 'COMPLETED') {
              jetPlanStatus = 'FAILED';
            }

            leg = {...leg, planData: fuel.planData, ...legChanges}

            if (leg.acmiCost) {
              let contingencyHoursAmount = Math.round((leg.acmiCost * 100 * ((contingencyBlockHoursRate||0) / 100)))/100;
              let contingencyFuelAmount = Math.round((leg.fuelCost * 100 * ((contingencyFuelCostRate||0) / 100)))/100;

              leg['contingencyHoursAmount'] = contingencyHoursAmount || 0;
              leg['contingencyFuelAmount'] = contingencyFuelAmount || 0;

              const totalCost = leg.acmiCost + leg.fuelCost + leg.permitCosts + leg.overflightCost + leg.groundCost + leg.contingencyHoursAmount + leg.contingencyFuelAmount;
              leg['totalCost'] = totalCost;
            }

            return leg;
          });
          const revisedLegs = rebuildRevenue(legs, contingencyBlockHoursRate);
          const totals = rebuildTotals(revisedLegs, costingErrors, state?.record?.costFilters||{});

          recordChange({legs: revisedLegs, ...totals, costingErrors, jetPlanStatus, acmiRate});
          SystemData.toggleLoadingProgress(false);
        }
      }).catch(err => {
        SystemData.alert("Error", `Fuel analysis failed to run. ${err}`);
        SystemData.toggleLoadingProgress(false);
      });
    }

    eventBus.bind("AddCostingDialog.open", open);
    eventBus.bind("AddCostingDialog.close", close);
    eventBus.bind("AddCostingDialog.quickRoute", quickRoute);
    eventBus.bind("AddCostingDialog.calculateRoute", calculateRoute);
    eventBus.bind("AddCostingDialog.buildRoute", buildRoute);
    eventBus.bind("AddCostingDialog.calculateRouteProgress", calculateRouteProgress);
    eventBus.bind("AddCostingDialog.runFuelAnalysis", runFuelAnalysis);
    eventBus.bind("AddCostingDialog.viewFuelAnalysis", viewFuelAnalysis);
    eventBus.bind("AddCostingDialog.recalculate", recalculate);
    eventBus.bind("AddCostingDialog.toggleFilters", toggleFilters);

    return () => {
      eventBus.unbind("AddCostingDialog.open", open);
      eventBus.unbind("AddCostingDialog.close", close);
	  eventBus.unbind("AddCostingDialog.quickRoute", quickRoute);
      eventBus.unbind("AddCostingDialog.calculateRoute", calculateRoute);
      eventBus.unbind("AddCostingDialog.buildRoute", buildRoute);
      eventBus.unbind("AddCostingDialog.calculateRouteProgress", calculateRouteProgress);
      eventBus.unbind("AddCostingDialog.runFuelAnalysis", runFuelAnalysis);
      eventBus.unbind("AddCostingDialog.viewFuelAnalysis", viewFuelAnalysis);
      eventBus.unbind("AddCostingDialog.recalculate", recalculate);
      eventBus.unbind("AddCostingDialog.toggleFilters", toggleFilters);
    }
  }, [dialogStates, eventBus, queryClient, SystemData, apiSitePrefix, costingErrors, rebuildRunning, costCategories, filtersEnabled]);

  const userOptions = departmentUsers.data?departmentUsers.data.map((userOption, i) => {
    return {code: userOption.user_id, value: `${userOption.firstName} ${userOption.lastName}`};
  }).sort((u1, u2) => {
    if (u1.value === u2.value) return 0;
    if (u1.value < u2.value) return -1;
    return 1;
  }):null;

  const processServicesRebuild = async () => {

    if (rebuildRunning) return true;

    setRebuildRunning(true);
    let nextRebuildToRun = null;
    for (let i = 0; i < rebuildServicesQueue.length; i++) {
      nextRebuildToRun = rebuildServicesQueue.pop();
    }
    if (!nextRebuildToRun) {
      setRebuildRunning(false);
      return true;
    }

    const {legs, services, aircraftReg, recordChange, cargo, changes, contingencyFuelCostRate, contingencyBlockHoursRate, costingDate, costFilters} = nextRebuildToRun;

    const {services: newServices, legs: newLegs} = await rebuildService(legs, costCategories, queryClient, services, aircraftReg, cargo, contingencyFuelCostRate, contingencyBlockHoursRate, undefined, costingDate, costFilters);
    const revisedLegs = rebuildRevenue(newLegs, contingencyBlockHoursRate);
    const totals = rebuildTotals(revisedLegs, costingErrors, costFilters);
    if (recordChange) recordChange({services: newServices, legs: revisedLegs, cargo, ...changes, ...totals});
    eventBus.trigger(`FormDialog.${formId}.changeRecord`, {changes: {services: newServices, legs: revisedLegs, cargo, ...changes, ...totals}});

    if (rebuildServicesQueue.length > 0) {
      setTimeout(processServicesRebuild, 1);
      return true;
    }
    setRebuildRunning(false);
    return true;
  }

  const handleAircraftChanged = (aircraftReg, props) => {
    const {record, recordChange} = props;

    if (aircraftReg && aircraftReg !== record.aircraftReg) {
      fetchAcmiRate({queryClient, extraKeys:[aircraftReg, record.costingDate], useCache: true}).then((acmiRate) => {
        const {legs, cargo} = rebuildPayloads(record.legs, record.cargo);
        const oldLegList = Array.isArray(legs) && legs.length > 0?[legs[0].originAirportCode, ...legs.map(leg => leg.destinationAirportCode)]:[];
        let services = __.cloneDeep(record?.services);
        const legsClone = __.cloneDeep(legs);
        const {contingencyFuelCostRate, contingencyBlockHoursRate} = record;
        rebuildServicesQueue.push({oldLegList, legs: legsClone, services, aircraftReg, recordChange, cargo, changes:{aircraftReg, acmiRate}, contingencyFuelCostRate, contingencyBlockHoursRate, costingDate: record.costingDate, costFilters: record.costFilters});
        recordChange({aircraftReg, acmiRate, legs, cargo});
        setTimeout(processServicesRebuild, 1);
      }).catch(err => {
        if (err?.response?.data?.error && err?.response?.data?.errorMessage) {
          SystemData.alert("Error", `${err?.response?.data?.errorMessage}`);
        } else {
          SystemData.alert("Error", `An unexpected error has occurred after changing the aircraft`);
        }
      })
    }
  }

  const handleCargoChanged = (changes, props) => {
    const {record, recordChange} = props;

    if (changes && changes.record) {
      const {legs, cargo} = rebuildPayloads(record.legs, changes.record.cargo);
      const oldLegList = Array.isArray(legs) && legs.length > 0?[legs[0].originAirportCode, ...legs.map(leg => leg.destinationAirportCode)]:[];
      let services = __.cloneDeep(record?.services);
      const legsClone = __.cloneDeep(legs);
      const {contingencyFuelCostRate, contingencyBlockHoursRate} = record;
      rebuildServicesQueue.push({oldLegList, legs: legsClone, services, aircraftReg: record.aircraftReg, recordChange, cargo, contingencyFuelCostRate, contingencyBlockHoursRate, costingDate: record.costingDate, costFilters: record.costFilters});
      eventBus.trigger(`FormDialog.${formId}.changeRecord`, {changes: {legs, cargo}});
      // recordChange({legs, cargo});
      if (changes.resolve) {
        changes.resolve();
      }
      setTimeout(processServicesRebuild, 1);
    }
  }

  const handleSectorsChanged = (changes, props) => {
    const {record, recordChange} = props;
    queueSectorChanges(changes, record, recordChange, eventBus, rebuildServicesQueue, processServicesRebuild, formId, costingErrors);
  }

  const handleRevenueChanged = (legs, record) => {
    const totals = rebuildTotals(legs, costingErrors, record.costFilters);
    eventBus.trigger(`FormDialog.${formId}.changeRecord`, {changes: {legs, ...totals}});
  }

  const handleTotalsChanged = (changes, props) => {
    const {record, recordChange} = props;
    if (!__.has(changes, 'contingencyFuelCostRate')) changes.contingencyFuelCostRate = record.contingencyFuelCostRate;
    if (!__.has(changes, 'contingencyBlockHoursRate')) changes.contingencyBlockHoursRate = record.contingencyBlockHoursRate;
    const totals = rebuildTotals(changes.legs, costingErrors, record.costFilters);
    recordChange({...changes, ...totals});
  }

  const handleBuildRouteFromSelection = async (changes, params) => {
    const {record, recordChange} = params;
    const {option} = changes;

    const newLegs = await buildLegsFromSuggestion(option, queryClient, record);
    const services = [];
    const oldLegList = [];

    const {legs, cargo} = rebuildPayloads(newLegs, record.cargo);

    const legsClone = __.cloneDeep(legs);
    recordChange({legs, cargo, services});

    for (let i = 0; i < legs.length; i++) {
      runSectorJetPlan(record, legs[i], apiSitePrefix);
    }
    const {contingencyFuelCostRate, contingencyBlockHoursRate} = record;
    rebuildServicesQueue.push({oldLegList, legs: legsClone, services, aircraftReg: record.aircraftReg, recordChange, cargo, contingencyFuelCostRate, contingencyBlockHoursRate, costingDate: record.costingDate, costFilters: record.costFilters});
    setTimeout(processServicesRebuild, 1);
  }

  const handleProcessQuickRouteSelection = async (changes, params) => {
    const {record, recordChange} = params;
    const {option, otherRoutes} = changes;

    const newLegs = await buildLegsFromSuggestion(option, queryClient, record);
    //TODO: Need to rebuild other routes from otherRoutes

    const services = [];
    const oldLegList = [];

    const {legs, cargo} = rebuildPayloads(newLegs, record.cargo);
    const legsClone = __.cloneDeep(legs);
    recordChange({legs, cargo, services});

    for (let i = 0; i < legs.length; i++) {
      runSectorJetPlan(record, legs[i], apiSitePrefix);
    }
    const {contingencyFuelCostRate, contingencyBlockHoursRate} = record;
    rebuildServicesQueue.push({oldLegList, legs: legsClone, services, aircraftReg: record.aircraftReg, recordChange, cargo, contingencyFuelCostRate, contingencyBlockHoursRate, costingDate: record.costingDate, costFilters: record.costFilters});
    setTimeout(processServicesRebuild, 1);
  }

  const handleRouteSelection = (option) => {
    eventBus.trigger(`FormDialog.${formId}.changeRecord`, {changes: {option}, onChangeHandler: handleBuildRouteFromSelection});
  }

  const handleQuickRouteSelection = (params) => {
    eventBus.trigger(`FormDialog.${formId}.changeRecord`, {changes: params, onChangeHandler: handleProcessQuickRouteSelection});
  }

  const handleOptimalRouteEndpointSelected = (params) => {
    const {record, finalDestinationAirport} = params;
    eventBus.trigger(`AddCostingDialog.buildRoute`, {record, finalDestinationAirport});
  }

  if (dialogStates.showDialog && departmentUsers?.data && costCategories?.data && costingErrors?.data) {
    return (
      <>
        <FormDialog
          formId={formId}
          title={dialogStates.title}
          message=""
          open={dialogStates.showDialog}
          record={dialogStates.record}
          dialogOptions={{maxWidth: "lg", fullWidth: true}}
          classes={classes}
          validator={Validator}
          buttons = {{
            toggleFiltersOn:{icon: "list", key: "Show Filters", label: "Show Filters", color: "secondary", hidden: filtersEnabled === true},
            toggleFiltersOff:{icon: "list", key: "Hide Filters", label: "Hide Filters", color: "secondary", hidden: filtersEnabled !== true},
            recalculate:{icon: "redo", key: "Recalculate", label: "Recalculate", color: "secondary", disabled: (status)=>{return status.error || rebuildRunning}},
            save:{icon: "check_circle", key: "Save", label: "Save", color: "primary", disabled: (status)=>{return status.error || rebuildRunning}},
            cancel:{icon: "cancel", key: "Cancel", label: "Cancel", color: "primary"}
          }}
          onChange={(state) => {
            if (state.buttonPressed === "recalculate") {
              eventBus.trigger("AddCostingDialog.recalculate", {record: state.record, recordChange: state.recordChange});
            } else if (state.buttonPressed === "toggleFiltersOn") {
              eventBus.trigger("AddCostingDialog.toggleFilters", {record: state.record, recordChange: state.recordChange, filtersEnabled: true});
            } else if (state.buttonPressed === "toggleFiltersOff") {
              eventBus.trigger("AddCostingDialog.toggleFilters", {record: state.record, recordChange: state.recordChange, filtersEnabled: false});
            } else if (state.buttonPressed === "save") {
              const record = state.record;
              const scratchPad = props.source==='FLIGHT PLANNING';
              handleAdd.mutate({
                extraKeys,
                record: {...record, scratchPad},
                postSuccess: (savedCosting) => {
                  setDialogStates({...dialogStates, showDialog: false, record: __.cloneDeep(emptyCosting)})
                  if(props.afterSave) props.afterSave({...record, ...savedCosting?.record?.data});
                },
                postError: (data) => {
                  if (data.errorMessage) {
                    SystemData.alert("Error", `${data.errorMessage}`);
                  } else {
                    SystemData.alert("Error", `An Unexpected Error has occurred.`);
                  }
                }
              });
            } else {
              setDialogStates({...dialogStates, showDialog: false, record: __.cloneDeep(emptyCosting)});
            }
          }}

        >
          <Grid container checkChildren={true} spacing={3}>
            {/* <Grid item xs={1} checkChildren={true}>
              <MaskedText
                id="uniqueCostingNo"
                label="Costing Number"
                style={{width: "100%"}}
                mask="UUU-9999999"
                maskChar={""}
                alwaysShowMask={false}
                forceUpperCase={true}
                defaultValue=""
                inputLabelProps={{shrink: true}}
                onChange={(value, props) => {
                  const requestNo = ++requestRef.current;
                  axios.get(
                    `${apiSitePrefix}/mcsdata/costingexists/${value}/___`
                  ).then(costingExists => {
                    if (requestNo === requestRef.current) {
                      if (costingExists.data) {
                        props.recordChange({tripRoute: costingExists.data.routing, uniqueCostingNo: value});
                      }
                    }
                  });
                  props.onChange(value);
                }}
              />
            </Grid> */}
            <Grid item xs={2} checkChildren={true}>
              {(record) => (
                <AircraftLookup
                  label="Aircraft"
                  id="aircraftReg"
                  inputLabelProps={{shrink: true}}
                  value={record.aircraftReg}
                  onChange={handleAircraftChanged}
                />
              )}
            </Grid>
            <Grid item xs={2} checkChildren={true} style={{marginTop: 1}}>
              {record =>
                <FormSelect
                  id="commercialOwnerId"
                  label="Account Manager"
                  options={userOptions}
                  readOnly={false}
                  value={record.commercialOwnerId}
                  onChange={(value, props) => {
                    props.onChange(value);
                  }}
                  style={{width: 200}}
                />
              }
            </Grid>
            <Grid item xs={2} checkChildren={true} style={{marginTop: 1}}>
            {record =>
              <ComboSelect
                id="routeWinds"
                label="Route Wind"
                group='COSTING'
                subGroup='ROUTE WIND'
                returnType="value"
                inputLabelProps={{shrink: true}}
                onChange={(value, props) => {
                  const {record, recordChange} = props;
                  let routeWindsMonth = "";
                  if (value === "HISTORICAL-WX") {
                    routeWindsMonth = `${moment().month()+1}`;
                  }
                  recordChange({...record, routeWinds:value, routeWindsMonth});
                }}
              />
            }
            </Grid>
            <Grid item xs={2} checkChildren={true} style={{marginTop: 1}}>
            {record =>
              <ComboSelect
                id="routeWindsMonth"
                label="Historical Month"
                group='MONTHS'
                subGroup='NUMBER'
                returnType="value"
                inputLabelProps={{shrink: true}}
                disabled={record.routeWinds !== "HISTORICAL-WX"}
              />
            }
            </Grid>
            <Grid item xs={2} checkChildren={true} style={{marginTop: 1}}>
            {record =>
              <ComboSelect
                id="revenueCalculationMethod"
                label="Revenue Model"
                group='COSTING REVENUE METHODS'
                subGroup=''
                returnType="value"
                inputLabelProps={{shrink: true}}
              />
            }
            </Grid>
          </Grid>
          <Grid container checkChildren={true} spacing={3}>
            <Grid item xs={3} checkChildren={true}>
              <div className="d-flex flex-row justify-content-between align-items-center">
                <label htmlFor="primaryRoute" className="MuiFormLabel-root MuiInputLabel-root MuiInputLabel-animated MuiInputLabel-shrink">
                  Route
                </label>
              </div>
              <div id="primaryRoute" style={{paddingTop: 6}}>
                {(rowData) => (<BuildPrimaryRouteFromLegs legs={rowData.legs}/>)}
              </div>
            </Grid>
            <Grid item xs={2} checkChildren={true}>
              <div className="d-flex flex-row justify-content-between align-items-center">
                <label htmlFor="onwardRoutes" className="MuiFormLabel-root MuiInputLabel-root MuiInputLabel-animated MuiInputLabel-shrink">
                  Onward Routes
                </label>
              </div>
              <div id="onwardRoutes" style={{paddingTop: 6}}>
                {(rowData) => (<BuildOnwardRoutesFromLegs legs={rowData.legs}/>)}
              </div>
            </Grid>
            <Grid item xs={2} checkChildren={true}>
            <div className="d-flex flex-row justify-content-between align-items-center">
                <label htmlFor="truckRoutes" className="MuiFormLabel-root MuiInputLabel-root MuiInputLabel-animated MuiInputLabel-shrink">
                  Trucking
                </label>
              </div>
              <div id="truckRoutes" style={{paddingTop: 6}}>
                {(rowData) => (<BuildTruckRoutesFromLegs legs={rowData.legs}/>)}
              </div>
            </Grid>
            <Grid item xs={12} checkChildren={true}>
              <div>
                {(record) => (
                  <CostingCargoEditor
                    record={record}
                    // handleUpdate={handleAdd}
                    extraKeys={extraKeys}
                    canEdit={true}
                    classes={classes}
                    onChange={handleCargoChanged}
                    defaultValues={defaultValues}
                    weightUom={record.weightUom}
                  />
                )}
                {(record) => (
                  <ActionsBar
                    eventPrefix="CostingCargoEditor"
                    record={record}
                    state={{}}
                    formDialog={{addRecordChange: true}}
                    actions={[
                      {label: 'Quick Route', key: 'Quick Route', type: 'button', color: "primary", disabled: (state) => {return !(Array.isArray(state?.record?.cargo) && state.record.cargo.length > 0) || !state?.record?.aircraftReg}, eventName: 'AddCostingDialog.quickRoute'},
                      {label: 'Calculate Route', key: 'Calculate Route', type: 'button', icon: "local_gas_station", color: "primary", disabled: (state) => {return !(record.aircraftReg && Array.isArray(state?.record?.cargo) && state.record.cargo.length > 0) || !state?.record?.aircraftReg}, eventName: 'AddCostingDialog.calculateRoute'},
                      {label: 'Add Consignment', key: 'Add Consignment', type: 'button', icon: "add_circle", color: "primary", disabled: (state) => {return !record.aircraftReg}, eventName: 'CostingCargoEditor.addCargo'}
                    ]}
                  />
                )}
              </div>
            </Grid>
            <Grid item xs={12} checkChildren={true}>
              <div>
                {(record) => (
                  <CostingSectorsEditor
                    record={record}
                    handleUpdate={handleAdd}
                    extraKeys={extraKeys}
                    canEdit={true}
                    classes={classes}
                    onChange={handleSectorsChanged}
                    defaultValues={defaultValues}
                    weightUom={record.weightUom}
                  />
                )}
                {(record) => {
                  const actions = [];
                  if (record.jetPlanStatus === 'COMPLETED' || record.jetPlanStatus === 'FAILED') {
                    actions.push({label: 'View Fuel Analysis', key: 'View Fuel Analysis', type: 'button', icon: "local_gas_station", color: "primary", disabled: (state) => {return !(record.aircraftReg && Array.isArray(state?.record?.legs) && state.record.legs.length > 0)}, eventName: 'AddCostingDialog.viewFuelAnalysis'});
                  }
                  actions.push({label: 'Run Fuel Analysis', key: 'Run Fuel Analysis', type: 'button', icon: "local_gas_station", color: "secondary", disabled: (state) => {return !(record.aircraftReg && Array.isArray(state?.record?.legs) && state.record.legs.length > 0)}, eventName: 'AddCostingDialog.runFuelAnalysis'});
                  actions.push({label: 'Add Sector', key: 'Add Sector', type: 'button', icon: "add_circle", color: "primary", disabled: (state) => {return !state.record.aircraftReg}, eventName: 'CostingSectorsEditor.addSector'});
                  return (
                    <ActionsBar
                      eventPrefix="CostingSectorsEditor"
                      record={record}
                      state={{}}
                      formDialog={{addRecordChange: true}}
                      actions={actions}
                    />
                  )
                }}
              </div>
            </Grid>
            <Grid item xs={12} checkChildren={true}>
              <div>
                {(record) => (
                  <CostingOtherSectorsEditor
                    record={record}
                    handleUpdate={handleAdd}
                    extraKeys={extraKeys}
                    canEdit={true}
                    classes={classes}
                    onChange={handleSectorsChanged}
                    defaultValues={defaultValues}
                    weightUom={record.weightUom}
                  />
                )}
                {(record) => (
                  <ActionsBar
                    eventPrefix="CostingOtherSectorsEditor"
                    record={record}
                    state={{}}
                    formDialog={{addRecordChange: true}}
                    actions={[
                      {label: 'Add Truck', key: 'Add Truck', type: 'button', icon: "local_shipping", color: "primary", disabled: (state) => {return !state.record.aircraftReg}, eventName: 'CostingOtherSectorsEditor.addTruck'},
                      {label: 'Add Onward Flight', key: 'Add Onward Flight', type: 'button', icon: "flight_takeoff", color: "primary", disabled: (state) => {return !state.record.aircraftReg}, eventName: 'CostingOtherSectorsEditor.addOnward'}
                    ]}
                  />
                )}
              </div>
            </Grid>
            <Grid item xs={12} checkChildren={true}>
              {(record) => (
                <FlightCostsView
                  record={record}
                  classes={classes}
                  defaultValues={defaultValues}
                  weightUom={record.weightUom}
                  filtersEnabled={filtersEnabled}
                  onChange={(changes, props) => handleFlightCostsChanged({costingErrors, changes}, props)}
                />
              )}
            </Grid>
            <Grid item xs={12} checkChildren={true}>
              {(record) => (
                <CostingGroundEditor
                  record={record}
                  eventBus={eventBus}
                  systemData={SystemData}
                  canEdit={true}
                  classes={classes}
                  onChange={(changes, props) => handleServicesChanged({costingErrors, changes}, props)}
                  defaultValues={defaultValues}
                  rebuildRunning={rebuildRunning}
                  filtersEnabled={filtersEnabled}
                />
              )}
            </Grid>
            <Grid item xs={12} checkChildren={true}>
              {(record) => (
                <CostingCommissionEditor
                  record={record}
                  eventBus={eventBus}
                  systemData={SystemData}
                  canEdit={true}
                  classes={classes}
                  onChange={handleCommissionsChanged}
                  defaultValues={defaultValues}
                  rebuildRunning={rebuildRunning}
                  costingErrors={costingErrors}
                />
              )}
              {(record) => (
                <ActionsBar
                  eventPrefix="CostingCommissionEditor"
                  record={record}
                  state={{}}
                  formDialog={{addRecordChange: true}}
                  actions={[
                    {label: 'Add Commission', key: 'Add Commission', type: 'button', icon: "monetization_on", color: "primary", eventName: 'CostingCommissionEditor.addCommission'},
                  ]}
                />
              )}
            </Grid>
            <Grid item xs={12} checkChildren={true}>
              {(record) => (
                <CostingRevenueEditor
                  record={record}
                  eventBus={eventBus}
                  systemData={SystemData}
                  canEdit={true}
                  classes={classes}
                  onChange={(legs) => handleRevenueChanged(legs, record)}
                  defaultValues={defaultValues}
                  rebuildRunning={rebuildRunning}
                />
              )}
            </Grid>
            <Grid item xs={12} checkChildren={true}>
              {(record) => (
                <CostingTotals
                  record={record}
                  eventBus={eventBus}
                  systemData={SystemData}
                  canEdit={true}
                  classes={classes}
                  defaultValues={defaultValues}
                  rebuildRunning={rebuildRunning}
                  costingErrors={costingErrors}
                  onChange={handleTotalsChanged}
                />
              )}
            </Grid>
          </Grid>
        </FormDialog>
        <RouteSelector
          SystemData={SystemData}
          onChange={handleRouteSelection}
        />
        <GetRouteEndpointDialog
          SystemData={SystemData}
          eventPrefix="GetRouteEndpointDialog"
          onChange={handleOptimalRouteEndpointSelected}
        />
        <ViewFuelAnalysis
          SystemData={SystemData}
          eventPrefix="ViewFuelAnalysis"
        />
        <QuickRoute
          legs={dialogStates?.record?.legs}
          SystemData={SystemData}
          queryClient={queryClient}
          onChange={handleQuickRouteSelection}
        />
      </>
    )
  }
  return (<></>);
}

export default AddCostingDialog;
